import { useParams } from "react-router-dom";
import { useGetCustomerDetailsQuery } from "@/redux/services/customer.api";
import {
  formatPhoneNumber,
  getInitials,
  formatCompactPrice,
  formatRelativeDate,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { formatCustomerSince } from "@/lib/customers";
import {
  Phone,
  ShoppingBag,
  CalendarDays,
  TrendingUp,
  Mail,
} from "lucide-react";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import DashLayout from "@/layouts/DashLayout";
import { Roles } from "@/redux/services/user/user.type";
import OrderCard from "@/components/order/OrderCard";
import BackLink from "@/components/BackLink";

export default function CustomerDetails() {
  const { customerId } = useParams<{ customerId: string }>();
  const currency = useRestaurantCurrency();

  const { data: customer, isLoading } = useGetCustomerDetailsQuery(
    customerId!,
    {
      skip: !customerId,
    }
  );

  if (isLoading) {
    return (
      <DashLayout>
        <RoleProtectedPage
          allowedRoles={[Roles.cashier, Roles.manager, Roles.owner]}
          allowedPermissions={["customers.view"]}
        >
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </RoleProtectedPage>
      </DashLayout>
    );
  }

  if (!customer) {
    return (
      <DashLayout>
        <RoleProtectedPage
          allowedRoles={[Roles.cashier, Roles.manager, Roles.owner]}
          allowedPermissions={["customers.view"]}
        >
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="text-center bg-red-50 border-l-4 border-red-800 rounded py-4">
              Client introuvable
            </div>
          </div>
        </RoleProtectedPage>
      </DashLayout>
    );
  }

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.cashier, Roles.manager]}
        allowedPermissions={["customers.view"]}
      >
        <div className="space-y-6">
          <div>
            <BackLink to="/customers" label="Retour aux clients" />

            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-semibold text-primary">
                  {getInitials(customer.fullName)}
                </span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-card-foreground">
                  {customer.fullName ?? "Pas défini"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {formatCustomerSince(customer.firstOrderAt)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Contact Info Card */}
            <div className="bg-card rounded-lg border border-border p-4 group hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">
                    Contact
                  </h3>
                  <p className="text-base font-medium text-card-foreground">
                    {formatPhoneNumber(customer.phone)}
                  </p>
                  {customer.email && (
                    <div className="flex items-center gap-1 mt-1">
                      <Mail className="w-3 h-3 text-gray-400" />
                      <p className="text-sm text-gray-600 truncate">
                        {customer.email}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Total Orders Card */}
            <div className="bg-card rounded-lg border border-border p-4 group hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center group-hover:bg-success/20 transition-colors">
                  <ShoppingBag className="w-5 h-5 text-success" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">
                    Total Commandes
                  </h3>
                  <p className="text-2xl font-bold text-card-foreground">
                    {customer.totalOrders}
                  </p>
                </div>
              </div>
            </div>

            {/* Total Spent Card */}
            <div className="bg-card rounded-lg border border-border p-4 group hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-900/10 rounded-lg flex items-center justify-center group-hover:bg-purple-900/20 transition-colors">
                  <TrendingUp className="w-5 h-5 text-purple-900" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">
                    Total dépensé
                  </h3>
                  <p className="text-2xl font-bold text-card-foreground">
                    {formatCompactPrice(customer.totalSpent, currency)}
                  </p>
                </div>
              </div>
            </div>

            {/* Activity Card */}
            <div className="bg-card rounded-lg border border-border p-4 group hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-thblue/10 rounded-lg flex items-center justify-center group-hover:bg-thblue/20 transition-colors">
                  <CalendarDays className="w-5 h-5 text-thblue" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">
                    Dernière visite
                  </h3>
                  <div className="space-y-1">
                    <div>
                      <p className="text-sm font-medium text-card-foreground">
                        {customer.lastOrderAt
                          ? formatRelativeDate(new Date(customer.lastOrderAt))
                          : "-"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order History Section */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-card-foreground">
                Historique des commandes
              </h2>
            </div>

            {customer.orders && customer.orders.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {customer.orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    searchOrderNumber=""
                    readonly
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-muted-foreground">Aucune commande trouvée</p>
                <p className="text-sm text-gray-400 mt-1">
                  Les commandes apparaîtront ici
                </p>
              </div>
            )}
          </div>
        </div>
      </RoleProtectedPage>
    </DashLayout>
  );
}
